import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, CircularProgress, Alert, Tabs, Tab, Paper, IconButton,
  Tooltip, Switch, FormControlLabel, Checkbox, Divider, Grid,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DownloadIcon from "@mui/icons-material/Download";
import adminApi from "../api/adminApi";

// Terms, privacy policy and FAQ of the new website (BackendNode routes/pages.js). While a page is
// not published, the website shows its own text. "Import" copies the website's current text here
// (from <website>/texts/pages-<lang>.json) as a starting point.
const LOCALES = [
  { code: "en", label: "English" },
  { code: "pt", label: "Português" },
  { code: "es", label: "Español" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "pl", label: "Polski" },
];
const PAGES = [
  { slug: "terms", label: "Terms and conditions" },
  { slug: "privacy", label: "Privacy policy" },
  { slug: "faq", label: "FAQ" },
];
const SITE_KEY = "site_texts_website";
const DEFAULT_SITE = "https://www.whynotcarrental.com";
const NAVY = "#09233f";

// Example values for the preview; the website uses its real ones (siteInfo.js).
const SAMPLE_VARS = {
  company: "Why Not Car Rental, Lda.", email: "info@whynotcarrental.com", frontOffice: "frontoffice@whynotcarrental.com",
  phone: "+351 291 000 000", funchal: "Rua Ivens 12, Funchal", santaCruz: "Santa Cruz, near Madeira Airport",
  open: "08:30", close: "18:00", years: "40", since: "1985", days: "180",
};
const VARIABLES = Object.keys(SAMPLE_VARS).map((v) => `{{${v}}}`).join(" ");

const errorText = (err, fallback) => err.response?.data?.message || err.message || fallback;
const emptyLegal = () => Object.fromEntries(LOCALES.map((l) => [l.code, { title: "", sections: [] }]));
const fill = (text) => String(text || "").replace(/\{\{\s*(\w+)\s*\}\}/g, (m, n) => SAMPLE_VARS[n] ?? m);
const move = (list, from, to) => {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

/** Same rules as the website (src/features/customPages/format.js parseBody). */
function Body({ text }) {
  const blocks = [];
  for (const chunk of String(text || "").replace(/\r\n?/g, "\n").split(/\n\s*\n/)) {
    let paragraph = [];
    let list = null;
    const flush = () => { if (paragraph.length) blocks.push({ p: paragraph.join(" ") }); paragraph = []; };
    for (const line of chunk.split("\n").map((l) => l.trim()).filter(Boolean)) {
      if (/^[-•]\s+/.test(line)) { flush(); list = list || { ul: [] }; list.ul.push(line.replace(/^[-•]\s+/, "")); }
      else { if (list) { blocks.push(list); list = null; } paragraph.push(line); }
    }
    flush();
    if (list) blocks.push(list);
  }
  return blocks.map((b, i) =>
    b.ul ? (
      <ul key={i} style={{ margin: "0 0 12px", paddingLeft: 22 }}>{b.ul.map((it, j) => <li key={j}>{fill(it)}</li>)}</ul>
    ) : (
      <p key={i} style={{ margin: "0 0 12px" }}>{fill(b.p)}</p>
    )
  );
}

const LegalPreview = ({ doc }) => (
  <Box sx={{ p: 3, bgcolor: "#fff", border: "1px solid #dfe5eb", borderRadius: 3, color: NAVY, fontSize: 14, lineHeight: 1.6 }}>
    <Typography variant="h4" fontWeight={800} sx={{ mb: 2, fontFamily: "Georgia, serif" }}>{doc.title || "—"}</Typography>
    {doc.sections.map((s, i) => (
      <Box key={i} sx={{ mb: 2 }}>
        {s.title && <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>{s.title}</Typography>}
        <Body text={s.body} />
      </Box>
    ))}
  </Box>
);

const FaqPreview = ({ items, lang }) => (
  <Box sx={{ p: 2, bgcolor: "#fff", border: "1px solid #dfe5eb", borderRadius: 3, color: NAVY }}>
    {items.map((item, i) => {
      const qa = item.content[lang]?.q ? item.content[lang] : item.content.en;
      return (
        <details key={item.id} open={i === 0} style={{ borderBottom: "1px solid #eef1f4", padding: "10px 4px" }}>
          <summary style={{ fontWeight: 700, cursor: "pointer" }}>{fill(qa.q) || "—"}</summary>
          <Box sx={{ mt: 1, fontSize: 14, whiteSpace: "pre-line" }}>{fill(qa.a)}</Box>
        </details>
      );
    })}
  </Box>
);

const LegalFaqManager = () => {
  const [slug, setSlug] = useState("terms");
  const [lang, setLang] = useState("pt");
  const [website, setWebsite] = useState(() => localStorage.getItem(SITE_KEY) || DEFAULT_SITE);
  const [form, setForm] = useState(null); // { published, content } | { published, items }
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const isFaq = slug === "faq";

  const load = async (which) => {
    setLoading(true);
    setMessage(null);
    try {
      const { data } = await adminApi.get(`/pages/${which}/draft`);
      const page = data.page;
      setForm(
        which === "faq"
          ? { published: Boolean(page?.published), items: page?.items || [] }
          : { published: Boolean(page?.published), content: { ...emptyLegal(), ...(page?.content || {}) } }
      );
      setDirty(false);
      if (!page) setMessage({ severity: "info", text: "Nothing written here yet: the website shows its own text. Use “Import the website text” to start from it." });
    } catch (err) {
      setMessage({ severity: "error", text: errorText(err, "Could not load the page") });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(slug); }, [slug]);

  const change = (update) => { setForm((prev) => update(prev)); setDirty(true); };

  const importFromWebsite = async () => {
    if (dirty || (isFaq ? form.items.length : LOCALES.some((l) => form.content[l.code]?.sections?.length))) {
      if (!window.confirm("Replace what is written here with the website's current text, in every language?")) return;
    }
    try {
      const base = website.replace(/\/+$/, "");
      localStorage.setItem(SITE_KEY, website);
      const all = Object.fromEntries(
        await Promise.all(
          LOCALES.map(async ({ code }) => {
            const res = await fetch(`${base}/texts/pages-${code}.json`, { cache: "no-store" });
            if (!res.ok) throw new Error(`${base}/texts/pages-${code}.json answered ${res.status}`);
            return [code, await res.json()];
          })
        )
      );
      if (isFaq) {
        change((prev) => ({
          ...prev,
          items: all.en.faq.map((en) => ({
            id: en.id,
            home: en.home,
            content: Object.fromEntries(LOCALES.map(({ code }) => {
              const own = all[code].faq.find((f) => f.id === en.id) || {};
              return [code, { q: own.q || "", a: own.a || "" }];
            })),
          })),
        }));
      } else {
        change((prev) => ({ ...prev, content: Object.fromEntries(LOCALES.map(({ code }) => [code, all[code][slug]])) }));
      }
      setMessage({ severity: "success", text: "Imported. Check the texts, then save (and publish when ready)." });
    } catch (err) {
      setMessage({ severity: "error", text: `Could not import (${errorText(err, "error")}). Check the website address.` });
    }
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const { data } = await adminApi.put(`/pages/${slug}`, form);
      setForm(isFaq ? { published: data.page.published, items: data.page.items } : { published: data.page.published, content: data.page.content });
      setDirty(false);
      setMessage({
        severity: "success",
        text: data.page.published
          ? "Saved and published. The website shows it now (reload the page); the next build puts it in the page HTML too."
          : "Saved as a draft. The website keeps showing its own text until you publish.",
      });
    } catch (err) {
      setMessage({ severity: "error", text: errorText(err, "Save failed") });
    } finally {
      setSaving(false);
    }
  };

  // ---- legal document editing (current language) ----
  const doc = !isFaq && form?.content ? form.content[lang] || { title: "", sections: [] } : null;
  const setDoc = (update) =>
    change((prev) => ({ ...prev, content: { ...prev.content, [lang]: update(prev.content[lang] || { title: "", sections: [] }) } }));
  const setSection = (i, field, value) =>
    setDoc((d) => ({ ...d, sections: d.sections.map((s, j) => (j === i ? { ...s, [field]: value } : s)) }));

  // ---- FAQ editing ----
  const setQa = (i, field, value) =>
    change((prev) => ({
      ...prev,
      items: prev.items.map((item, j) => (j === i ? { ...item, content: { ...item.content, [lang]: { ...item.content[lang], [field]: value } } } : item)),
    }));
  const addQuestion = () =>
    change((prev) => ({
      ...prev,
      items: [...prev.items, { id: `q${Date.now().toString(36)}`, home: false, content: Object.fromEntries(LOCALES.map((l) => [l.code, { q: "", a: "" }])) }],
    }));

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
        <Typography variant="h5" fontWeight="bold" color="#1C2541">Legal & FAQ</Typography>
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          {form && (
            <FormControlLabel
              label={form.published ? "Published" : "Draft"}
              control={<Switch checked={form.published} onChange={(e) => change((p) => ({ ...p, published: e.target.checked }))} />}
            />
          )}
          <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} onClick={save} disabled={saving || !form} sx={{ backgroundColor: "#1C2541" }}>
            Save{dirty ? " *" : ""}
          </Button>
        </Box>
      </Box>

      <Tabs value={slug} onChange={(e, v) => { if (!dirty || window.confirm("Leave without saving?")) setSlug(v); }} sx={{ mb: 2 }}>
        {PAGES.map((p) => <Tab key={p.slug} value={p.slug} label={p.label} />)}
      </Tabs>

      <Box display="flex" gap={1} alignItems="center" mb={2} flexWrap="wrap">
        <TextField size="small" label="Website address" value={website} onChange={(e) => setWebsite(e.target.value)} sx={{ minWidth: 320 }} />
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={importFromWebsite} disabled={!form}>Import the website text</Button>
      </Box>

      {message && <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      {loading || !form ? (
        <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>
      ) : (
        <>
          <Tabs value={lang} onChange={(e, v) => setLang(v)} variant="scrollable" sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
            {LOCALES.map((l) => <Tab key={l.code} value={l.code} label={l.code === "en" ? `${l.label} *` : l.label} />)}
          </Tabs>
          <Typography variant="body2" color="text.secondary" mb={2}>
            English is required. {isFaq ? "A question left empty in another language shows the English one." : "A language left empty shows the English version, with a note that it applies."}{" "}
            Text format: a blank line starts a new paragraph; a line starting with “- ” is a bullet. These are filled in by the website: {VARIABLES}
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              {isFaq ? (
                <>
                  {form.items.map((item, i) => (
                    <Paper key={item.id} elevation={1} sx={{ p: 2, mb: 2, borderRadius: 3 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography variant="subtitle2" color="#1C2541">Question {i + 1}</Typography>
                        <FormControlLabel
                          sx={{ ml: 1 }}
                          label="On the home page"
                          control={<Checkbox size="small" checked={item.home} onChange={(e) => change((p) => ({ ...p, items: p.items.map((it, j) => (j === i ? { ...it, home: e.target.checked } : it)) }))} />}
                        />
                        <Box flex={1} />
                        <Tooltip title="Up"><span><IconButton size="small" disabled={i === 0} onClick={() => change((p) => ({ ...p, items: move(p.items, i, i - 1) }))}><ArrowUpwardIcon fontSize="small" /></IconButton></span></Tooltip>
                        <Tooltip title="Down"><span><IconButton size="small" disabled={i === form.items.length - 1} onClick={() => change((p) => ({ ...p, items: move(p.items, i, i + 1) }))}><ArrowDownwardIcon fontSize="small" /></IconButton></span></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => window.confirm("Delete this question in every language?") && change((p) => ({ ...p, items: p.items.filter((_, j) => j !== i) }))}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </Box>
                      {lang !== "en" && <Typography variant="caption" color="text.secondary" display="block" mb={1}>EN: {item.content.en?.q}</Typography>}
                      <TextField fullWidth size="small" label="Question" value={item.content[lang]?.q || ""} onChange={(e) => setQa(i, "q", e.target.value)} sx={{ mb: 1 }} />
                      <TextField fullWidth size="small" label="Answer" multiline minRows={3} value={item.content[lang]?.a || ""} onChange={(e) => setQa(i, "a", e.target.value)} />
                    </Paper>
                  ))}
                  <Button startIcon={<AddIcon />} onClick={addQuestion}>Add a question</Button>
                </>
              ) : (
                <>
                  <TextField fullWidth label="Page title" value={doc.title} onChange={(e) => setDoc((d) => ({ ...d, title: e.target.value }))} sx={{ mb: 2 }} />
                  {doc.sections.map((s, i) => (
                    <Paper key={i} elevation={1} sx={{ p: 2, mb: 2, borderRadius: 3 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography variant="subtitle2" color="#1C2541">Section {i + 1}</Typography>
                        <Box flex={1} />
                        <Tooltip title="Up"><span><IconButton size="small" disabled={i === 0} onClick={() => setDoc((d) => ({ ...d, sections: move(d.sections, i, i - 1) }))}><ArrowUpwardIcon fontSize="small" /></IconButton></span></Tooltip>
                        <Tooltip title="Down"><span><IconButton size="small" disabled={i === doc.sections.length - 1} onClick={() => setDoc((d) => ({ ...d, sections: move(d.sections, i, i + 1) }))}><ArrowDownwardIcon fontSize="small" /></IconButton></span></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => window.confirm("Delete this section?") && setDoc((d) => ({ ...d, sections: d.sections.filter((_, j) => j !== i) }))}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </Box>
                      <TextField fullWidth size="small" label="Section title" value={s.title} onChange={(e) => setSection(i, "title", e.target.value)} sx={{ mb: 1 }} />
                      <TextField fullWidth size="small" label="Text" multiline minRows={4} value={s.body} onChange={(e) => setSection(i, "body", e.target.value)} />
                    </Paper>
                  ))}
                  <Button startIcon={<AddIcon />} onClick={() => setDoc((d) => ({ ...d, sections: [...d.sections, { title: "", body: "" }] }))}>Add a section</Button>
                </>
              )}
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box sx={{ position: "sticky", top: 16 }}>
                <Typography variant="subtitle2" color="#1C2541" mb={1}>Preview · {LOCALES.find((l) => l.code === lang).label}</Typography>
                <Divider sx={{ mb: 2 }} />
                {isFaq ? (
                  <FaqPreview items={form.items} lang={lang} />
                ) : (
                  <LegalPreview doc={doc.sections.length || doc.title ? doc : form.content.en} />
                )}
              </Box>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default LegalFaqManager;
